import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";
import { decimalSchema } from "@/utils/zod.ts";
import { LocalDate, YearMonth } from "@js-joda/core";
import { queryOptions } from "@tanstack/react-query";
import {
    type PageParams,
    createDataSelector,
    createResourceCollectionSelector,
    createResourceSelector,
    handleJsonApiError,
    injectPageParams,
} from "jsonapi-zod-query";
import { z } from "zod";
import { zj } from "zod-joda";

const standardParcelSchema = z.object({
    acreage: decimalSchema(),
    startingBid: z.number().int().nullable(),
    biddingIncrement: z.number().int().nullable(),
});

export type StandardParcel = z.output<typeof standardParcelSchema>;

const choiceParcelSchema = z.object({
    acreage: decimalSchema(),
});

export type ChoiceParcel = z.output<typeof choiceParcelSchema>;

const parcelCombinationSchema = z.object({
    parcelIndices: z.array(z.number().int()),
    name: z.string(),
    startingBid: z.number().int().nullable(),
    biddingIncrement: z.number().int().nullable(),
});

export type ParcelCombination = z.output<typeof parcelCombinationSchema>;

export const configSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("single_parcel"),
        parcel: standardParcelSchema,
    }),
    z.object({
        type: z.literal("multi_parcel"),
        parcels: z.array(standardParcelSchema),
    }),
    z.object({
        type: z.literal("combo"),
        parcels: z.array(standardParcelSchema),
        parcelCombinations: z.array(parcelCombinationSchema),
    }),
    z.object({
        type: z.literal("choice"),
        startingBid: z.number().int().nullable(),
        biddingIncrement: z.number().int().nullable(),
        parcels: z.array(choiceParcelSchema),
    }),
]);

const priceModelSchema = z.enum(["per_acre", "total"]);
export type PriceModel = z.output<typeof priceModelSchema>;

export const auctionAttributesSchema = z.object({
    type: z.enum(["single_parcel", "multi_parcel", "combo", "choice"]),
    sealedBid: z.boolean(),
    state: z.enum(["registration_open", "registration_closed", "running", "completed"]),
    priceModel: priceModelSchema,
    title: z.string(),
    description: z.string(),
    location: z.string().nullable(),
    startsAt: zj.zonedDateTime().transform((value) => value.withZoneSameInstant(localZoneId)),
    zoomMeetingUrl: z.string().url().nullable(),
    brochureUrl: z.string().url().nullable(),
    agentEmailAddress: z.string().email().nullable(),
    config: configSchema,
});

const auctionSelector = createDataSelector(
    createResourceSelector({
        type: "auction",
        attributesSchema: auctionAttributesSchema,
    }),
);

const auctionsSelector = createResourceCollectionSelector({
    type: "auction",
    attributesSchema: auctionAttributesSchema.pick({
        type: true,
        sealedBid: true,
        state: true,
        title: true,
        location: true,
        startsAt: true,
    }),
    metaSchema: z.object({
        numParcels: z.number().int().positive(),
        isStartable: z.boolean(),
    }),
});

export type Auction = ReturnType<typeof auctionSelector>;
export type PaginatedAuctions = ReturnType<typeof auctionsSelector>;
export type ListAuction = PaginatedAuctions["data"][number];

export type ListAuctionsOptions = {
    pageParams?: PageParams;
    status?: "active" | "completed";
    startsAtRange?: YearMonth | number | null;
};

export const createAuctionQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListAuctionsOptions) =>
        queryOptions({
            queryKey: ["auctions", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/auctions");
                url.searchParams.set(
                    "fields[auction]",
                    "type,sealedBid,state,title,location,startsAt",
                );
                injectPageParams(url, options?.pageParams);

                if (options?.status) {
                    url.searchParams.set("filter[status]", options.status);
                }

                if (options?.startsAtRange) {
                    url.searchParams.set(
                        "filter[startsAt][from]",
                        (options.startsAtRange instanceof YearMonth
                            ? options.startsAtRange.atDay(1)
                            : LocalDate.of(options.startsAtRange, 1, 1)
                        )
                            .atStartOfDay()
                            .atZone(localZoneId)
                            .toInstant()
                            .toString(),
                    );
                    url.searchParams.set(
                        "filter[startsAt][to]",
                        (options.startsAtRange instanceof YearMonth
                            ? options.startsAtRange.atEndOfMonth()
                            : LocalDate.of(options.startsAtRange, 12, 31)
                        )
                            .atStartOfDay()
                            .plusDays(1)
                            .minusNanos(1)
                            .atZone(localZoneId)
                            .toInstant()
                            .toString(),
                    );
                }

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return auctionsSelector(await response.json());
            },
        }),
    get: (auctionId: string) =>
        queryOptions({
            queryKey: ["auction", auctionId],
            queryFn: async ({ signal }) => {
                const response = await fetch(apiUrl(`/auctions/${auctionId}`), { signal });
                await handleJsonApiError(response);
                return auctionSelector(await response.json());
            },
        }),
});
